import { Flex } from '@chakra-ui/react';
import React from 'react';
import { z } from 'zod';

import {
  date as commonDate,
  typeguards as commonTypeguards,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';

// Types
export type FacilitiesTableRow = {
  creditFacilityName: string;
  borrowerEntityName: string;
  commitmentAmount: number;
  unusedCommitment: number;
  loanOutstanding: number;
  utilization: number;
  dateOfLastFundingRequest?: Util.FullDateStr;
  collectionsAccountBalance: number;
  borrowingBaseFromReceivables: number;
  borrowingBaseCashContribution: number;
  requiredReserve: number;
  totalBorrowingBase: number;
  interestRates: string;
  accruedInterestAndFees: number;
  financialCovenantsFailed: number | null; // 0 failed means pass, null means no calculations exist
  portfolioCovenantsFailed: number | null; // 0 failed means pass, null means no calculations exist
};

// Constants
export const FACILITIES_TABLE_CONFIG = {
  creditFacilityName: {
    accessorKey: 'creditFacilityName',
    header: 'Facility',
    fixed: true,
    parse: (cellValue: unknown) => z.string().parse(cellValue),
  },

  borrowerEntityName: {
    accessorKey: 'borrowerEntityName',
    header: 'Borrower Entity Name',
    fixed: false,
    parse: (cellValue: unknown) => z.string().parse(cellValue),
  },

  commitmentAmount: {
    accessorKey: 'commitmentAmount',
    header: 'Global Commitment amount',
    fixed: false,
    parse: (cellValue: unknown) => {
      const number = z.number().parse(cellValue);
      const formatted = commonUtils.numberToCurrencyWithSymbol({ number });
      return formatted ?? number;
    },
  },

  loanOutstanding: {
    accessorKey: 'loanOutstanding',
    header: 'Outstanding Balance',
    fixed: false,
    parse: (cellValue: unknown) => {
      const number = z.number().parse(cellValue);
      const formatted = commonUtils.numberToCurrencyWithSymbol({ number });
      return formatted ?? number;
    },
  },

  unusedCommitment: {
    accessorKey: 'unusedCommitment',
    header: 'Unused Commitment',
    fixed: false,
    parse: (cellValue: unknown) => {
      const number = z.number().parse(cellValue);
      const formatted = commonUtils.numberToCurrencyWithSymbol({ number });
      return formatted ?? number;
    },
  },

  utilization: {
    accessorKey: 'utilization',
    header: 'Utilization',
    fixed: false,
    parse: (cellValue: unknown) => {
      const number = z.number().parse(cellValue);
      const formatted = commonUtils.numberToFormattedString(
        number,
        'percentage',
        false,
      );
      return formatted ?? number;
    },
  },

  collectionsAccountBalance: {
    accessorKey: 'collectionsAccountBalance',
    header: 'Collections Account Balance',
    fixed: false,
    parse: (cellValue: unknown) => {
      const number = z.number().parse(cellValue);
      const formatted = commonUtils.numberToCurrencyWithSymbol({ number });
      return formatted ?? number;
    },
  },

  requiredReserve: {
    accessorKey: 'requiredReserve',
    header: 'Required Reserve',
    fixed: false,
    parse: (cellValue: unknown) => {
      const number = z.number().parse(cellValue);
      const formatted = commonUtils.numberToCurrencyWithSymbol({ number });
      return formatted ?? number;
    },
  },

  totalBorrowingBase: {
    accessorKey: 'totalBorrowingBase',
    header: 'Total Borrowing Base',
    fixed: false,
    parse: (cellValue: unknown) => {
      const number = z.number().parse(cellValue);
      const formatted = commonUtils.numberToCurrencyWithSymbol({ number });
      return formatted ?? number;
    },
  },

  borrowingBaseFromReceivables: {
    accessorKey: 'borrowingBaseFromReceivables',
    header: 'Borrowing Base from Receivables',
    fixed: false,
    parse: (cellValue: unknown) => {
      const number = z.number().parse(cellValue);
      const formatted = commonUtils.numberToCurrencyWithSymbol({ number });
      return formatted ?? number;
    },
  },

  borrowingBaseCashContribution: {
    accessorKey: 'borrowingBaseCashContribution',
    header: 'Borrowing Base from Cash',
    fixed: false,
    parse: (cellValue: unknown) => {
      const number = z.number().parse(cellValue);
      const formatted = commonUtils.numberToCurrencyWithSymbol({ number });
      return formatted ?? number;
    },
  },

  accruedInterestAndFees: {
    accessorKey: 'accruedInterestAndFees',
    header: 'Accrued Interest and Fees',
    fixed: false,
    parse: (cellValue: unknown) => {
      const number = z.number().parse(cellValue);
      const formatted = commonUtils.numberToCurrencyWithSymbol({ number });
      return formatted ?? number;
    },
  },

  interestRates: {
    accessorKey: 'interestRates',
    header: 'Interest Rate',
    fixed: false,
    parse: (cellValue: unknown) => {
      const string = z.string().parse(cellValue);
      return string;
    },
  },

  dateOfLastFundingRequest: {
    accessorKey: 'dateOfLastFundingRequest',
    header: 'Date of Last Funding Request',
    fixed: false,
    parse: (cellValue: unknown) => {
      return commonTypeguards.isFullDateStr(cellValue)
        ? commonDate.fullDateStrToHumanDateString(cellValue)
        : '-';
    },
  },

  financialCovenantsFailed: {
    accessorKey: 'financialCovenantsFailed',
    header: 'Financial Covenants',
    fixed: false,
    // eslint-disable-next-line react/display-name
    parse: (cellValue: unknown) => {
      if (typeof cellValue !== 'number') {
        return '-';
      }
      const isError = cellValue > 0;
      return (
        <Flex
          alignItems="center"
          background={isError ? '#FFF1F1' : '#ECFAF4'}
          borderRadius="0.5rem"
          color={isError ? colors.error : colors.success}
          fontWeight={500}
          gap="1rem"
          padding="0.5rem 1rem"
        >
          {isError ? <Icon icon="circle-x" /> : <Icon icon="circle-check" />}
          <span>{isError ? `${cellValue} Failed` : 'Passed'}</span>
        </Flex>
      );
    },
  },

  portfolioCovenantsFailed: {
    accessorKey: 'portfolioCovenantsFailed',
    header: 'Portfolio Covenants',
    fixed: false,
    // eslint-disable-next-line react/display-name
    parse: (cellValue: unknown) => {
      if (typeof cellValue !== 'number') {
        return '-';
      }
      const isError = cellValue > 0;
      return (
        <Flex
          alignItems="center"
          background={isError ? '#FFF1F1' : '#ECFAF4'}
          borderRadius="0.5rem"
          color={isError ? colors.error : colors.success}
          fontWeight={500}
          gap="1rem"
          padding="0.5rem 1rem"
        >
          {isError ? <Icon icon="circle-x" /> : <Icon icon="circle-check" />}
          <span>{isError ? `${cellValue} Failed` : 'Passed'}</span>
        </Flex>
      );
    },
  },
} as const;
export type FacilityTableColumnKey = keyof typeof FACILITIES_TABLE_CONFIG;
